import api from './api';
import { LD_JSON_ACCEPT, normalizeCollection, type CollectionResponse } from './hydraCollection';
import type { Plant, PlantFormData, HydraCollection } from '../types/entities';

const BASE_PATH = '/api/plants';

/**
 * Phase 35: "Werke, an denen der VDZ misst" -- serverseitig ueber den BooleanFilter auf der
 * verschachtelten Eigenschaft `kilns.allowsOwnMeasurements` (Plant.php). Wo das Anlegerecht beim
 * Werk liegt, ist kein Import vorgesehen (Sketch 019).
 */
const VDZ_MEASURED_QUERY = 'kilns.allowsOwnMeasurements=false';

export const plantsApi = {
  getAll: async (): Promise<Plant[]> => {
    const response = await api.get<HydraCollection<Plant> | Plant[]>(BASE_PATH);
    // Handle both JSON-LD (hydra) and plain JSON formats
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data['hydra:member'] ?? [];
  },

  /**
   * Die Werksliste der Importkaskade -- vollstaendig, aber ohne die Pagination dieser GETEILTEN
   * Stammdaten-Ressource abzuschalten: geholt wird Seite fuer Seite, bis die Gesamtzahl aus der
   * Antwort erreicht ist. Andere Verbraucher haengen an der Pagination, ein globales
   * `paginationEnabled: false` waere eine Aenderung fuer alle.
   */
  getWithVdzMeasuredKilns: async (): Promise<Plant[]> => {
    const collected: Plant[] = [];

    for (let page = 1; ; page += 1) {
      const response = await api.get<CollectionResponse<Plant>>(
        `${BASE_PATH}?${VDZ_MEASURED_QUERY}&page=${page}`,
        LD_JSON_ACCEPT
      );
      const collection = normalizeCollection(response.data);
      const members = collection['hydra:member'];
      collected.push(...members);

      // Abbruch auch bei einer leeren Seite -- sonst haengt die Schleife, falls die Gesamtzahl
      // einmal nicht zur gelieferten Menge passt.
      if (members.length === 0 || collected.length >= collection['hydra:totalItems']) {
        return collected;
      }
    }
  },

  getOne: async (id: number): Promise<Plant> => {
    const response = await api.get<Plant>(`${BASE_PATH}/${id}`);
    return response.data;
  },

  create: async (data: PlantFormData): Promise<Plant> => {
    const response = await api.post<Plant>(BASE_PATH, data);
    return response.data;
  },

  update: async (id: number, data: Partial<PlantFormData>): Promise<Plant> => {
    const response = await api.patch<Plant>(`${BASE_PATH}/${id}`, data, {
      headers: { 'Content-Type': 'application/merge-patch+json' },
    });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`${BASE_PATH}/${id}`);
  },
};
